import { AlertTriangle, ArrowRight, Check, KeyRound, LoaderCircle, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ActionCodeOperation, applyActionCode, checkActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import { authForEmailAction } from '../auth'
import { useApp } from '../app-context'
import { Logo } from '../components/Logo'

type ActionMode = 'verifyEmail'|'resetPassword'|'recoverEmail'|'verifyAndChangeEmail'|'revertSecondFactorAddition'
type ViewState =
  | {kind:'checking'}
  | {kind:'confirm';mode:Exclude<ActionMode,'resetPassword'>}
  | {kind:'reset';email:string}
  | {kind:'success';title:string;message:string}
  | {kind:'error';message:string}

const copyByMode:Record<Exclude<ActionMode,'resetPassword'>,{title:string;message:string}> = {
  verifyEmail:{title:'Email verified.',message:'Your account is ready. Sign in to continue your route.'},
  recoverEmail:{title:'Email restored.',message:'Your previous sign-in email has been restored. Reset your password if you did not request the change.'},
  verifyAndChangeEmail:{title:'Email updated.',message:'Your new sign-in email is confirmed and ready to use.'},
  revertSecondFactorAddition:{title:'Authenticator removed.',message:'The unrecognized authenticator was removed. Sign in and review your account security.'},
}

const confirmByMode:Record<Exclude<ActionMode,'resetPassword'>,{kicker:string;title:string;message:string;button:string}> = {
  verifyEmail:{kicker:'Email verification',title:'Verify your email.',message:'Confirm this email belongs to you to finish setting up your account.',button:'Verify email'},
  recoverEmail:{kicker:'Email recovery',title:'Restore your email.',message:'Confirm that you want to undo the recent sign-in email change.',button:'Restore previous email'},
  verifyAndChangeEmail:{kicker:'Email update',title:'Confirm your new email.',message:'Confirm this address before it becomes your rxrouting sign-in email.',button:'Confirm new email'},
  revertSecondFactorAddition:{kicker:'Account recovery',title:'Remove this authenticator.',message:'Confirm that you want to remove the recently added authenticator.',button:'Remove authenticator'},
}

const operationByMode:Record<Exclude<ActionMode,'resetPassword'>,string> = {
  verifyEmail:ActionCodeOperation.VERIFY_EMAIL,
  recoverEmail:ActionCodeOperation.RECOVER_EMAIL,
  verifyAndChangeEmail:ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL,
  revertSecondFactorAddition:ActionCodeOperation.REVERT_SECOND_FACTOR_ADDITION,
}

function actionMode(value:string|null):ActionMode|undefined {
  if(value==='verifyEmail'||value==='resetPassword'||value==='recoverEmail'||value==='verifyAndChangeEmail'||value==='revertSecondFactorAddition')return value
}

function clearActionQuery(){window.history.replaceState({},'',window.location.pathname)}

function unavailableMessage(mode:ActionMode|undefined){
  if(mode==='resetPassword')return 'This reset link is invalid, expired, or already used. Return to sign in and use Forgot password to request a new one.'
  if(mode==='verifyEmail')return 'This verification link is invalid, expired, or already used. Sign in to send a fresh verification email.'
  return 'This secure link is invalid, expired, or already used. Return to sign in to review your account security.'
}

export function AuthAction(){
  const {config}=useApp()
  const started=useRef(false)
  const params=new URLSearchParams(window.location.search)
  const mode=actionMode(params.get('mode'))
  const code=params.get('oobCode')||''
  const [state,setState]=useState<ViewState>({kind:'checking'})
  const [password,setPassword]=useState('')
  const [confirmation,setConfirmation]=useState('')
  const [formError,setFormError]=useState('')
  const [busy,setBusy]=useState(false)

  useEffect(()=>{
    if(started.current)return
    started.current=true
    const run=async()=>{
      if(!mode||!code){setState({kind:'error',message:'This account link is incomplete or invalid. Return to sign in to request a fresh email.'});return}
      try{
        const auth=authForEmailAction(config)
        if(mode==='resetPassword'){
          const email=await verifyPasswordResetCode(auth,code)
          setState({kind:'reset',email})
          return
        }
        const info=await checkActionCode(auth,code)
        if(info.operation!==operationByMode[mode])throw new Error('action mode mismatch')
        setState({kind:'confirm',mode})
      }catch{
        clearActionQuery()
        setState({kind:'error',message:unavailableMessage(mode)})
      }
    }
    void run()
  },[code,config,mode])

  const confirmAction=async()=>{
    if(state.kind!=='confirm'||busy)return
    setBusy(true)
    try{
      await applyActionCode(authForEmailAction(config),code)
      clearActionQuery()
      setState({kind:'success',...copyByMode[state.mode]})
    }catch{
      clearActionQuery()
      setState({kind:'error',message:unavailableMessage(state.mode)})
    }finally{setBusy(false)}
  }

  const reset=async(event:FormEvent)=>{
    event.preventDefault()
    if(state.kind!=='reset'||busy)return
    setFormError('')
    if(password.length<8){setFormError('Use at least 8 characters for your new password.');return}
    if(password!==confirmation){setFormError('The passwords do not match.');return}
    setBusy(true)
    try{
      await confirmPasswordReset(authForEmailAction(config),code,password)
      clearActionQuery()
      setState({kind:'success',title:'Password updated.',message:'Your new password is active. You can sign in now.'})
    }catch{
      clearActionQuery()
      setState({kind:'error',message:'This reset link expired or has already been used. Request a new one from sign in.'})
    }finally{setBusy(false)}
  }

  return <div className="auth-page auth-action-page">
    <aside className="auth-story auth-action-story">
      <Link to="/"><Logo/></Link>
      <div>
        <span className="auth-story__index">ACCOUNT SECURITY</span>
        <h2>One secure link. One clear result.</h2>
        <p>Account changes finish on rxrouting.com. Every link is single-use and checked before anything changes.</p>
        <div className="mini-label auth-action-label"><small>SECURE ACCOUNT ROUTE</small><b>EMAIL LINK</b><span>CHECK CODE</span><span><em>COMPLETE ACTION</em></span></div>
      </div>
      <small>rxrouting · account control</small>
    </aside>
    <main className="auth-form-wrap auth-action-wrap">
      <Link className="auth-action-mobile-brand" to="/"><Logo/></Link>
      <section className="auth-form auth-action-card" aria-live="polite">
        {state.kind==='checking'&&<ActionMessage icon={<LoaderCircle className="auth-action-spin"/>} kicker="Checking secure link" title="Just a moment." message="We are validating this account action."/>}
        {state.kind==='success'&&<><ActionMessage icon={<Check/>} kicker="Action complete" title={state.title} message={state.message} success/><a className="button button--primary button--large button--full" href="/login">Continue to sign in<ArrowRight size={18}/></a></>}
        {state.kind==='error'&&<><ActionMessage icon={<AlertTriangle/>} kicker="Link unavailable" title="This link is unavailable." message={state.message} error/><Link className="button button--primary button--large button--full" to="/login">Return to sign in<ArrowRight size={18}/></Link></>}
        {state.kind==='confirm'&&<><ActionMessage icon={<ShieldCheck/>} kicker={confirmByMode[state.mode].kicker} title={confirmByMode[state.mode].title} message={confirmByMode[state.mode].message}/><button className="button button--primary button--large button--full" disabled={busy} onClick={confirmAction}>{busy?'Completing action…':confirmByMode[state.mode].button}<ArrowRight size={18}/></button></>}
        {state.kind==='reset'&&<form onSubmit={reset} className="auth-action-reset">
          <ActionMessage icon={<KeyRound/>} kicker="Password reset" title="Choose a new password." message={`This reset applies to ${state.email}.`}/>
          <label>New password<input type="password" autoComplete="new-password" minLength={8} value={password} onChange={event=>setPassword(event.target.value)} required autoFocus/></label>
          <label>Confirm new password<input type="password" autoComplete="new-password" minLength={8} value={confirmation} onChange={event=>setConfirmation(event.target.value)} required/></label>
          {formError&&<div className="form-error" role="alert">{formError}</div>}
          <button className="button button--primary button--large button--full" disabled={busy}>{busy?'Updating password…':'Update password'}<ArrowRight size={18}/></button>
        </form>}
      </section>
    </main>
  </div>
}

function ActionMessage({icon,kicker,title,message,success=false,error=false}:{icon:React.ReactNode;kicker:string;title:string;message:string;success?:boolean;error?:boolean}){
  return <div className={`auth-action-message${success?' auth-action-message--success':''}${error?' auth-action-message--error':''}`}>
    <div className="auth-action-icon">{icon}</div>
    <div className="eyebrow"><span/>{kicker}</div>
    <h1>{title}</h1>
    <p>{message}</p>
    <div className="auth-action-security"><ShieldCheck size={17}/><span>For your security, each email link works only once.</span></div>
  </div>
}
